import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useState } from "react";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import { refundByIdApi } from "../../../services/billing.service";

export default function RefundPaymentModal({ open, onClose, payment, invoiceId, refetch }) {
  const [data, setData] = useState({
    amount: payment?.amountPaid || 0,
    reason: "",
    refundType: "full" // full or partial
  });
  const [loading, setLoading] = useState(false);

  // Reset form when modal is closed
  const handleClose = () => {
    setData({
      amount: payment?.amountPaid || 0,
      reason: "",
      refundType: "full"
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!data.reason.trim()) {
      toast.error("Please provide a reason for the refund");
      return;
    }

    if (data.amount <= 0) {
      toast.error("Refund amount must be greater than 0");
      return;
    }

    if (data.amount > payment?.amountPaid) {
      toast.error("Refund amount cannot exceed the payment amount");
      return;
    }

    setLoading(true);
    
    try {
      const response = await refundByIdApi(invoiceId, {
        amount: data.amount,
        reason: data.reason,
        paymentId: payment?._id,
        refundType: data.refundType
      });

      if (response.code === 200 || response.code === 201) {
        toast.success(`Payment refunded successfully! Amount: ₹${data.amount}`);
        handleClose();
        // Refresh the billing data to show updated refunds
        if (refetch) {
          refetch();
        }
      } else {
        toast.error(response?.message || "Failed to refund payment. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while processing the refund");
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setData(prev => ({
      ...prev,
      amount: value
    }));
  };

  const handleRefundTypeChange = (e) => {
    const refundType = e.target.value;
    setData(prev => ({
      ...prev,
      refundType,
      amount: refundType === "full" ? payment?.amountPaid || 0 : prev.amount
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "600px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        {/* dialog title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#5D6A82" }}>
            Refund Payment
          </Typography>
          <Close
            onClick={handleClose}
            sx={{ cursor: "pointer", color: "#5D6A82" }}
          />
        </Box>

        {/* Payment Details */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "white", borderRadius: "8px" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Payment Details:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Type: {payment?.transactionType?.name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Amount: ₹{payment?.amountPaid || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Date: {payment?.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : "-"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Remarks: {payment?.remarks || "-"}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: "4px" }}>
            <Typography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
              💡 <strong>Note:</strong> Payment refunds are now processed against the total payments made, 
              not limited by the current billing balance. You can refund up to the total amount paid by the patient.
            </Typography>
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #ddd" }}>
              <Typography variant="body2" sx={{ fontSize: "11px", color: "#888" }}>
                <strong>Payment Summary:</strong> Total Paid: ₹{payment?.amountPaid || 0} | 
                This refund will be processed against the total payments made to this invoice.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mb: "40px" }}>
          <Grid item xs={12}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Refund Type</InputLabel>
              <Select
                value={data.refundType}
                label="Refund Type"
                onChange={handleRefundTypeChange}
                sx={{ background: "white", borderRadius: "12px" }}
              >
                <MenuItem value="full">Full Refund</MenuItem>
                <MenuItem value="partial">Partial Refund</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Refund Amount"
              type="number"
              value={data.amount}
              onChange={handleAmountChange}
              fullWidth
              required
              disabled={data.refundType === "full"}
              inputProps={{ 
                min: 0, 
                max: payment?.amountPaid || 0,
                step: 0.01
              }}
              sx={{ background: "white", borderRadius: "12px" }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              multiline
              rows={3}
              label="Refund Reason"
              placeholder="Please provide a detailed reason for this refund..."
              value={data.reason}
              onChange={(e) => setData(prev => ({ ...prev, reason: e.target.value }))}
              required
              fullWidth
              sx={{ background: "white", borderRadius: "12px" }}
            />
          </Grid>
        </Grid>

        {/* submit button */}
        <Box sx={{ my: "20px", display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="outlined"
            sx={{ width: "150px", textTransform: "none" }}
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ width: "150px", textTransform: "none" }}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? <CircularProgress size={24} /> : `Refund`}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
